import {
  STTimeLineChartData,
  STTimeLineChartDataSeries,
} from '../monitoring.model';
import { CommanderMonitoringFilterLogicalObject } from './filter.model';
import { uniq } from 'lodash';
import { SensordataSTChartLine } from '../../../../../service/monitoring/monitoring.model';

export function getPaletteColors(
  paletteColors: ReturnType<typeof getPaletteColorsByControlUnitsDevices>,
  digitalChannels: STTimeLineChartData,
  sensordata: SensordataSTChartLine[]
) {
  const paletteColorsDigital = getPaletteColorsDigital(
    paletteColors,
    digitalChannels
  );
  const paletteColorsAnalog = getPaletteColorsAnalog(paletteColors, sensordata);
  return {
    paletteColorsDigital,
    paletteColorsAnalog,
  } as const;
}

function getPaletteColorsDigital(
  paletteColors: ReturnType<typeof getPaletteColorsByControlUnitsDevices>,
  digitalChannels: STTimeLineChartData
): string[] {
  const orderedListData = Object.values(digitalChannels)
    .slice()
    .sort(compareDigitalChannelsLikeLibrary);

  return orderedListData.map(
    (channel) => paletteColors[`${channel.deviceId}-${channel.controlUnitId}`]
  );
}

/**
 * Order digital channels the same way the ChartTimeline renders them:
 * channels that have data segments come first, empty channels last, and within
 * each group they are sorted by `name` ascending (case-insensitive) like
 * `SortUtil.multisort`.
 */
function compareDigitalChannelsLikeLibrary(
  channelA: STTimeLineChartDataSeries,
  channelB: STTimeLineChartDataSeries
): number {
  const isEmptyA = channelA.data.length === 0 ? 1 : 0;
  const isEmptyB = channelB.data.length === 0 ? 1 : 0;
  if (isEmptyA !== isEmptyB) return isEmptyA - isEmptyB;
  return compareLikeSortUtil(channelA.name, channelB.name);
}

/**
 * Compare two values the same way `SortUtil.multisort` does inside
 * stoerk-ui-components: numeric comparison when both values are numeric,
 * otherwise a case-insensitive string comparison. This keeps the color order
 * aligned with the row order the ChartTimeline renders.
 */
function compareLikeSortUtil(a: unknown, b: unknown): number {
  const isNumeric = !Number.isNaN(Number(a) - Number(b));
  let x: string | number = '';
  let y: string | number = '';
  if (a !== null && a !== undefined) {
    x = isNumeric ? Number(a) : String(a).toLowerCase();
  }
  if (b !== null && b !== undefined) {
    y = isNumeric ? Number(b) : String(b).toLowerCase();
  }
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}

function getPaletteColorsAnalog(
  paletteColors: ReturnType<typeof getPaletteColorsByControlUnitsDevices>,
  sensordata: SensordataSTChartLine[]
): string[] {
  return sensordata.map(
    (channel) => paletteColors[`${channel.deviceId}-${channel.controlunit}`]
  );
}

/**
 * Get palette colors by control units and devices involved
 * Only one color for each control units
 * @param digitalFilterObjectEvents
 * @param analogFilterObjectEvents
 *
 */
export function getPaletteColorsByControlUnitsDevices(
  digitalFilterObjectEvents: CommanderMonitoringFilterLogicalObject,
  analogFilterObjectEvents: CommanderMonitoringFilterLogicalObject
): { [deviceId_controlUnitId: string]: string } {
  /** 1. get all possible keys by control unit */
  const controlUnitKeys = [
    ...getKeysControlUnitsDevicesFromFilterObject(digitalFilterObjectEvents),
    ...getKeysControlUnitsDevicesFromFilterObject(analogFilterObjectEvents),
  ];
  /** 2. remove duplicates */
  const uniqControlUnitKeys = uniq(controlUnitKeys);

  /** 3. get palette colors - Predefined by flexeserve */
  const newPaletteColors = [
    '#d94d14',
    '#0070c0',
    '#1e7d3f',
    '#333333',
    '#ffcb71',
  ];

  /** 4. create dic to associate each color with control unit */
  return uniqControlUnitKeys.reduce((acc, key, index) => {
    acc[key] = newPaletteColors[index % newPaletteColors.length];
    return acc;
  }, {} as { [deviceId_controlUnitId: string]: string });
}

function getKeysControlUnitsDevicesFromFilterObject(
  filterObjectEvents: CommanderMonitoringFilterLogicalObject
): string[] {
  return Object.entries(filterObjectEvents)
    .map(([deviceId, filterObjectDeviceEvents]) =>
      Object.keys(filterObjectDeviceEvents ?? {}).map(
        (controlUnitId) => `${deviceId}-${controlUnitId}`
      )
    )
    .flat();
}

export default getPaletteColors;
